// components/FileProcessing/FileProcessingModal.tsx
import React from 'react';
import ClipLoader from 'react-spinners/ClipLoader';
import { FileProgress } from '../../../hooks/useFileProcessing';
import { FileProgressCard } from './FileProgressCard';

interface FileProcessingModalProps {
    isVisible: boolean;
    currentPhase: string;
    currentFileProgress: FileProgress | null;
    currentFileIndex: number;
    totalFiles: number;
    isEditing?: boolean;
}

export const FileProcessingModal: React.FC<FileProcessingModalProps> = ({
    isVisible,
    currentPhase,
    currentFileProgress,
    currentFileIndex,
    totalFiles,
    isEditing = false
}) => {
    if (!isVisible) return null;

    return (
        <>
            {/* Backdrop overlay */}
            <div className="fixed inset-0 z-50 bg-white/80 backdrop-blur-sm" />

            {/* Modal */}
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="bg-eq-super-light-grey rounded-sm shadow-lg pointer-events-auto max-w-lg w-full max-h-[80vh] overflow-y-auto transition-all duration-500 ease-in-out">
                    <div className="px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                        {/* Upload Icon Header */}
                        <div className="flex items-start gap-4 mb-4">
                            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                                <span className="material-symbols-outlined text-[1.5rem] text-eq-green">upload_file</span>
                            </div>
                            <div className="flex-1">
                                <h3 className="text-base font-semibold leading-6 text-eq-grey">
                                    {isEditing ? 'Your assistant is being updated...' : 'Your assistant is being created...'}
                                </h3>
                                <p className="text-sm text-eq-grey mt-2">
                                    {currentPhase || 'Processing...'}
                                </p>
                            </div>
                        </div>
                        <div
                            className={`flex justify-center transition-all duration-500 ease-in-out ${!currentFileProgress
                                    ? 'max-h-[100px] opacity-100 mb-4'
                                    : 'max-h-0 opacity-0 mb-0'
                                }`}
                        >
                            {!currentFileProgress && (
                                <ClipLoader size={40} color="#6D3E91" />
                            )}
                        </div>
                        <div
                            className={`overflow-hidden transition-all duration-500 ease-in-out ${currentFileProgress
                                    ? 'max-h-[500px] opacity-100'
                                    : 'max-h-0 opacity-0'
                                }`}
                        >
                            {currentFileProgress && (
                                <div
                                    key={`${currentFileProgress.filename}-${currentFileIndex}`}
                                    className="text-left"
                                    style={{
                                        animation: 'fadeIn 0.5s ease-in-out',
                                    }}
                                >
                                    <FileProgressCard progress={currentFileProgress} />
                                </div>
                            )}
                        </div>

                        <p className="text-sm text-eq-grey mt-4">
                            This may take a few minutes depending on the number and size of files submitted.
                            <br />
                            Please keep this window open.
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
};
